import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { isNativeApp } from "@/lib/platform";

const SubscriptionSuccess = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const handleSuccess = async () => {
      const sessionId = searchParams.get("session_id");
      const canceled = searchParams.get("canceled");

      // Check if user canceled
      if (canceled === "true") {
        toast.error(t('notifications.toast.payment_was_canceled_no_charges_were_made'));
        setTimeout(() => navigate("/"), 100);
        return;
      }

      // If no session_id, something went wrong
      if (!sessionId) {
        toast.error(t('notifications.toast.invalid_session_please_try_again'));
        setTimeout(() => navigate("/"), 100);
        return;
      }

      // Invalidate only subscription-related queries, preserve profile data
      console.log('[SubscriptionSuccess] Invalidating subscription cache (preserving profile)');
      queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["user-credits"] });
      
      // Trigger immediate subscription sync by dispatching event
      // This ensures SubscriptionSync component syncs immediately
      window.dispatchEvent(new CustomEvent("force-subscription-sync"));
    };

    handleSuccess();
  }, [searchParams, navigate, queryClient]);

  // Auto-redirect countdown for web (non-native) after successful payment
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    if (!sessionId || canceled === "true") return; // Only run for success page
    
    const isWeb = !isNativeApp();
    if (!isWeb) return; // Only auto-redirect on web
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [searchParams, navigate]);

  // Show success message - user should restart the app
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">{t('ui.text.congratulations')}</h1>
          <p className="text-lg text-muted-foreground mb-6">{t('ui.text.your_subscription_was_successful')}</p>
        </div>
        {isNativeApp() ? (
          <>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-4">
              <p className="text-lg font-semibold mb-2">{t('ui.text.next_step')}</p>
              <p className="text-muted-foreground">
                Please close and restart the GitFit.AI app to activate your premium features.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">{t('ui.text.your_subscription_is_now_active_and_will_be_availa')}</p>
          </>
        ) : (
          <>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-4">
              <p className="text-lg font-semibold mb-2">{t('ui.text.success')}</p>
              <p className="text-muted-foreground mb-3">{t('ui.text.your_premium_subscription_is_now_active')}</p>
              <p className="text-sm text-muted-foreground">
                Redirecting to dashboard in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
            <Button onClick={() => navigate("/", { replace: true })} className="w-full">{t('ui.text.go_to_dashboard_now')}</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
